import numeral from 'numeral';

export const NumeralTypes = {
  DEFAULT: 'NUMBER',
  NUMBER: 'NUMBER',
  DECIMAL: 'DECIMAL',
  PERCENTAGE: 'PERCENTAGE',
  PERCENT_4: 'PERCENT_4',
  PERCENT_2: 'PERCENT_2',
} as const;

export type NumeralType = (typeof NumeralTypes)[keyof typeof NumeralTypes];

export const FormatTypes = {
  DEFAULT: '(0,0.[00000])',
  [NumeralTypes.DECIMAL]: '(0,0.00)',
  [NumeralTypes.PERCENTAGE]: '0.000000%',
  [NumeralTypes.PERCENT_4]: '0.0000%',
  [NumeralTypes.PERCENT_2]: '0.00%',
} as const;

const FormatPrecisions = {
  DEFAULT: 2,
  PERCENTAGE: 8,
  PERCENT_4: 6,
};


const hasDefinedFormat = (type: string): type is keyof typeof FormatTypes => type in FormatTypes;
export const getFormat = (type: NumeralType) => hasDefinedFormat(type) ? FormatTypes[type] : FormatTypes.DEFAULT;

const hasDefinedPrecision = (type: string): type is keyof typeof FormatPrecisions => type in FormatPrecisions;
export const getFixedPrecision = (type: NumeralType): number => hasDefinedPrecision(type)
  ? FormatPrecisions[type]
  : FormatPrecisions.DEFAULT;

type FormatValueParams<T extends boolean> = {
  value: string | number | null | undefined,
  type: NumeralType,
  customFormat?: string | null,
  fixedPrecision?: number | null,
  acceptRaw?: T,
}

type FormatValueResult<T extends boolean> = T extends true
    ? string | number | null | undefined
    : string | null;

export const formatValue = <T extends boolean = false>({
  value,
  type,
  customFormat,
  fixedPrecision,
  acceptRaw,
}: FormatValueParams<T>): FormatValueResult<T> => {
  const format = customFormat
    ? customFormat
    : getFormat(type);

  const toFixedPrecision = fixedPrecision
    ? fixedPrecision
    : getFixedPrecision(type);

  const parseValue = (value: FormatValueParams<T>['value']) =>
    value === null || value === undefined // eslint-disable-line no-undefined
      ? null
      : numeral(Number(value).toFixed(toFixedPrecision)).format(format);

  if (typeof acceptRaw === 'boolean' && acceptRaw) {
    if (isNaN(parseFloat(value as any))) {
      return value as FormatValueResult<T>;
    }
  }

  return parseValue(value) as FormatValueResult<T>;
};
